import { Stack } from "@/core/components/ui/stack";
import { ActionStatusGlyph } from "@/accounts-payable/common";
import type { ApAgentAction } from "@/accounts-payable/types";
import {
  actionCategoryLabel,
  actionDetail,
  automatedOutcomeCopy,
} from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";

export const AutomatedActionRow = ({
  action,
  showDivider,
}: {
  action: ApAgentAction;
  /** False for the first row — the header band already draws that edge. */
  showDivider: boolean;
}) => {
  const outcome = automatedOutcomeCopy(action);
  // Only an apply failure reads red; other deviations are muted info.
  const isApplyFailure = action.status === "apply_failed";
  return (
    <Stack gap="none" fullWidth className="px-6">
      <Stack
        direction="row"
        gap="sm"
        align="start"
        justify="between"
        fullWidth
        className={cn("py-3", {
          "border-t border-stroke-tertiary": showDivider,
        })}
      >
        {/* The outcome glyph leads the row like a checklist mark; the right side
            stays open for per-row actions we may add later (retry, view in
            ServiceTitan). */}
        <Stack
          direction="row"
          gap="sm"
          align="center"
          className="min-w-0 flex-1"
        >
          <ActionStatusGlyph
            action={action}
            outline
            className="h-5 w-5 flex-none"
          />
          <Stack gap="none" className="min-w-0">
            {/* Eyebrow: the action category as a neutral caption on top, the
                specific target below it. One neutral color for every type — a
                per-type hue read as status (red like an error) when it was only
                meant to differentiate. */}
            <span className="text-sm font-semibold text-secondary-text">
              {actionCategoryLabel(action)}
            </span>
            <span className="text-md">
              {actionDetail(action)}
              {/* The outcome is appended only when it deviates from a clean
                  apply; only an apply failure reads red. */}
              {outcome && (
                <span
                  className={cn({
                    "text-negative-text": isApplyFailure,
                    "text-tertiary-text": !isApplyFailure,
                  })}
                >
                  {" · "}
                  {outcome}
                </span>
              )}
            </span>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
